import { useRouter } from 'next/router';
import { useSimulateContract, useWriteContract } from 'wagmi';

import TransactionModal from 'components/TransactionModal';
import Message from 'components/base/Message';
import Modal from 'components/base/Modal';
import useTransactionStore, {
  useTransactionEffects,
} from 'state/stores/transactions';

import useModalVisibility from 'hooks/use-modal-visibility';
import { NFTCollection } from 'lib/abis/NFTCollection';
import { extractPrepareContractWriteRevertReason } from 'utils/revert-reasons';
import { selectTransactionByAction } from 'utils/transaction-tracking';

import { ModalOptions } from 'types/modal';

const MODAL_KEY = 'SELF_DESTRUCT_COLLECTION';
const ACTION = 'self-destruct-collection';

type SelfDestructModalOptions = ModalOptions<typeof MODAL_KEY>;

export default function SelfDestructModal() {
  const modal = useModalVisibility(MODAL_KEY);

  return (
    <Modal.Root open={modal.open} onOpenChange={modal.onOpenChange}>
      <Modal.Portal>
        <Modal.BlurOverlay />
        <Modal.PositionOverlay>
          <Modal.UnmountListener onUnmount={modal.onUnmount} />
          {modal.config && <SelfDestructModalWindow {...modal.config} />}
        </Modal.PositionOverlay>
      </Modal.Portal>
    </Modal.Root>
  );
}

function SelfDestructModalWindow(props: SelfDestructModalOptions) {
  const { contractAddress, chainId } = props;

  const txStore = useTransactionStore();

  const router = useRouter();

  const simulateSelfDestruct = useSimulateContract({
    abi: NFTCollection,
    functionName: 'selfDestruct',
    address: contractAddress,
    chainId,
    query: {
      retry: false,
    },
  });

  const contractWrite = useWriteContract({
    mutation: {
      onSuccess: (txHash) => {
        txStore.startTracking({
          chainId,
          ui: 'toast',
          action: ACTION,
          txHash,
          title: {
            PENDING: 'Self-destructing Collection…',
            SUCCESS: 'Collection self-destructed',
          },
          description: {
            SUCCESS:
              'This collection has successfully self-destructed, and it will no longer be displayed on Foundation.',
          },
        });
      },
    },
  });

  const trackedTx = useTransactionStore(
    selectTransactionByAction({
      action: ACTION,
      txHash: contractWrite.data || null,
    })
  );

  useTransactionEffects(trackedTx, {
    onSuccess: () => {
      router.replace('/');
    },
  });

  const errorReason =
    extractPrepareContractWriteRevertReason(simulateSelfDestruct);

  return (
    <TransactionModal
      chainId={chainId}
      preSignPrompt={{
        type: 'skip',
      }}
    >
      <TransactionModal.Content
        action={ACTION}
        chainId={chainId}
        txHash={contractWrite.data || null}
        footer={
          <Modal.Footer>
            <TransactionModal.TransactionButton
              chainId={chainId}
              isLoading={contractWrite.isPending || Boolean(contractWrite.data)}
              isDisabled={!simulateSelfDestruct.isSuccess}
              write={() => {
                if (simulateSelfDestruct.isSuccess) {
                  contractWrite.writeContract(
                    simulateSelfDestruct.data.request
                  );
                }
              }}
              error={errorReason}
              label="Self-destruct"
            />
          </Modal.Footer>
        }
      >
        <TransactionModal.Body>
          <Modal.BodyTitle
            align="left"
            title="Self-destruct this collection"
            description="Self-destructing a collection destroys the collection and removes it
            from your creator profile."
          />
          <Message
            title="Please note"
            message=" — once completed, this action cannot be reversed."
          />
        </TransactionModal.Body>
      </TransactionModal.Content>
    </TransactionModal>
  );
}
